import { type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { BillingCharge } from '@kawai/shared';
import {
  formatYen,
  formatCardLabel,
  formatCardExpiry,
  billingChargeKindLabel,
  billingChargeStatusLabel,
  billingSubscriptionStatusLabel,
  isOfflinePaymentMethod,
  resolveBankTransferDisplayStatus,
  bankTransferDisplayStatusLabel,
  todayJst,
} from '@kawai/shared';
import { Chip, ZebraTable } from '@kawai/ui';
import { useTenant } from '../lib/hooks/useTenant.js';
import { useBillingSubscription } from '../lib/hooks/useBillingSubscription.js';
import { useBillingCharges } from '../lib/hooks/useBillingCharges.js';
import { useAuth } from '../lib/auth.js';
import {
  billingStatusChipTone,
  chargeStatusChipTone,
  bankTransferStatusChipTone,
} from '../lib/billingStyles.js';
import { CARD, CARD_TITLE, MONO } from '../lib/uiClasses.js';
import { PageHeader } from '../components/PageHeader.js';
import { Notice } from '../components/Notice.js';

/** ISO8601(JST) を `YYYY-MM-DD HH:mm` に整形（文字列は JST 前提のため slice で十分）。 */
function formatJstDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

/** 帳票（請求書 / 領収書）へのリンク。ゼブラ行の上なので面を持たせず文字色で示す。 */
const DOC_LINK = 'text-label font-medium text-brand-ink underline underline-offset-2';

/**
 * TP-25: テナント別の決済明細ページ（saas-admin 運営者ビュー / 閲覧専用）。
 *
 * - 現在の登録カード（brand / 下 4 桁 / 有効期限）を表示（tenants.payjpDefaultCard）
 * - サブスク状態（billing_subscriptions）と期末 / 解約予約を表示
 * - 請求履歴（billing_charges）を新しい順に一覧表示（種別 / 金額 / 日時 / 結果 / 取引 ID）
 *
 * カード登録 / 変更はテナント自身が tenant-portal /billing で行う（TP-20）。本ページは
 * 運営者の閲覧用で write は持たない。tenant / subscription は一覧と同じ全件 hook を再利用し、
 * 請求履歴のみ tenantId スコープで新規取得する。アクセスは <SaasAdminGuard> で saas-admin に限定。
 * S-137: 見た目は docs/UI-GUIDE.md の v2（白カード 30/26・ZebraTable・Chip）。
 */
export function BillingDetailPage() {
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const { email, signOut } = useAuth();

  // 明細は 1 テナント分なので単一 doc 取得（全件取得 + client filter を避けて O(1) read）。
  const {
    data: tenant,
    isLoading: tenantLoading,
    error: tenantError,
  } = useTenant(tenantId);
  const { data: sub } = useBillingSubscription(tenantId);
  const {
    data: charges,
    isLoading: chargesLoading,
    error: chargesError,
  } = useBillingCharges(tenantId);

  // 課金状態 fallback は 1 度だけ導出し、バッジ色と表示テキストで同じ値を使う。
  const billingStatus = tenant?.billingStatus ?? 'none';

  return (
    <main className="mx-auto max-w-[1100px] p-6">
      <PageHeader
        title="決済明細"
        description={
          <>
            テナント <span className={MONO}>{tenantId}</span> のカード / 請求履歴（閲覧）
          </>
        }
        email={email}
        links={[{ to: '/billing', label: '課金管理へ戻る' }]}
        onSignOut={() => {
          void signOut();
        }}
      />

      {tenantLoading && (
        <p role="status" aria-live="polite" className="mt-6 text-body text-ink-3">
          読み込み中…
        </p>
      )}

      {(tenantError || (!tenantLoading && !tenantError && !tenant)) && (
        <section className={`mt-6 ${CARD}`}>
          {tenantError && (
            <Notice role="alert">
              テナント情報の取得に失敗しました: {tenantError.message}
            </Notice>
          )}
          {!tenantLoading && !tenantError && !tenant && (
            <Notice role="alert">
              テナント &quot;{tenantId}&quot; が見つかりません。
            </Notice>
          )}
        </section>
      )}

      {tenant && (
        <>
          <section className={`mt-6 ${CARD}`} aria-label="テナント概要">
            <h2 className={CARD_TITLE}>テナント概要</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryItem label="テナント名" value={tenant.name} />
              <SummaryItem
                label="課金状態"
                value={
                  <Chip tone={billingStatusChipTone(billingStatus)}>{billingStatus}</Chip>
                }
              />
              <SummaryItem
                label="登録カード"
                value={
                  tenant.payjpDefaultCard ? (
                    <>
                      {formatCardLabel(tenant.payjpDefaultCard)}
                      <span className="ml-1 text-label text-ink-3">
                        （有効期限 {formatCardExpiry(tenant.payjpDefaultCard)}）
                      </span>
                    </>
                  ) : (
                    <span className="text-ink-3">未登録</span>
                  )
                }
              />
              <SummaryItem
                label="サブスク状態"
                value={
                  sub ? (
                    <>
                      {billingSubscriptionStatusLabel(sub.status)}
                      {sub.currentPeriodEnd && (
                        <span className="ml-1 text-label text-ink-3">
                          （期末 {sub.currentPeriodEnd.slice(0, 10)}）
                        </span>
                      )}
                      {sub.status === 'canceled' && !sub.endedAt && (
                        <span className="ml-2 text-note font-medium text-warning-ink">
                          解約予約中
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-ink-3">未開始</span>
                  )
                }
              />
            </div>
            <p className="mt-4 text-label text-ink-3">
              カードの登録 /
              変更はテナント自身が「お支払い設定」（tenant-portal）で行います。
              本画面は運営者の閲覧用です。
            </p>
          </section>

          <section className={`mt-6 ${CARD}`} aria-label="請求履歴">
            <h2 className={CARD_TITLE}>請求履歴</h2>

            {chargesLoading && (
              <p role="status" aria-live="polite" className="mt-4 text-body text-ink-3">
                請求履歴を読み込み中…
              </p>
            )}

            {chargesError && (
              <Notice role="alert" className="mt-4">
                請求履歴の取得に失敗しました: {chargesError.message}
              </Notice>
            )}

            {!chargesLoading && !chargesError && (
              <div className="mt-4 overflow-x-auto">
                <ZebraTable aria-label="請求履歴">
                  <thead>
                    <ZebraTable.HeadRow>
                      <ZebraTable.Th edge="first">日時</ZebraTable.Th>
                      <ZebraTable.Th>種別</ZebraTable.Th>
                      <ZebraTable.Th align="right">金額</ZebraTable.Th>
                      <ZebraTable.Th>結果</ZebraTable.Th>
                      <ZebraTable.Th>取引 ID</ZebraTable.Th>
                      <ZebraTable.Th>説明</ZebraTable.Th>
                      <ZebraTable.Th edge="last">帳票</ZebraTable.Th>
                    </ZebraTable.HeadRow>
                  </thead>
                  <tbody>
                    {(charges ?? []).length === 0 ? (
                      <ZebraTable.Row>
                        <ZebraTable.Td
                          colSpan={7}
                          align="center"
                          tone="muted"
                          padding="lg"
                        >
                          請求履歴がありません
                        </ZebraTable.Td>
                      </ZebraTable.Row>
                    ) : (
                      (charges ?? []).map((c) => (
                        <ZebraTable.Row key={c.id} className="align-top">
                          <ZebraTable.Td className="whitespace-nowrap">
                            {formatJstDateTime(c.createdAt)}
                          </ZebraTable.Td>
                          <ZebraTable.Td>{billingChargeKindLabel(c.kind)}</ZebraTable.Td>
                          <ZebraTable.Td numeric>{formatYen(c.amountYen)}</ZebraTable.Td>
                          <ZebraTable.Td>
                            {/* TP-27: 振込請求はカード用ラベル（保留/失敗）だと誤読される
                                （pending=請求済 / failed=取消 / 滞納は期限派生）ため振込表記で出す。 */}
                            <ChargeStatusChip charge={c} />
                          </ZebraTable.Td>
                          <ZebraTable.Td className={MONO}>
                            {c.payjpChargeId ?? '—'}
                          </ZebraTable.Td>
                          <ZebraTable.Td>{c.description ?? '—'}</ZebraTable.Td>
                          <ZebraTable.Td>
                            <div className="flex gap-3 whitespace-nowrap">
                              <Link
                                to={`/billing/${tenantId}/invoice/${c.id}?type=invoice`}
                                target="_blank"
                                className={DOC_LINK}
                              >
                                請求書
                              </Link>
                              {c.status === 'succeeded' && (
                                <Link
                                  to={`/billing/${tenantId}/invoice/${c.id}?type=receipt`}
                                  target="_blank"
                                  className={DOC_LINK}
                                >
                                  領収書
                                </Link>
                              )}
                            </div>
                          </ZebraTable.Td>
                        </ZebraTable.Row>
                      ))
                    )}
                  </tbody>
                </ZebraTable>
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
}

interface SummaryItemProps {
  label: string;
  value: ReactNode;
}
function SummaryItem({ label, value }: SummaryItemProps) {
  return (
    <div className="grid content-start gap-1 rounded-[10px] bg-surface-2 px-5 py-4">
      <div className="text-label font-medium text-ink-2">{label}</div>
      <div className="text-body text-ink">{value}</div>
    </div>
  );
}

/** 請求 1 行の結果チップ。振込・口座振替は表示派生ステータス（TP-27/TP-34）、カードは従来ラベル。 */
function ChargeStatusChip({ charge }: { charge: BillingCharge }) {
  if (isOfflinePaymentMethod(charge.paymentMethod)) {
    const display = resolveBankTransferDisplayStatus(
      charge.status,
      charge.dueDate,
      todayJst(),
    );
    return (
      <Chip tone={bankTransferStatusChipTone(display)}>
        {bankTransferDisplayStatusLabel(display, charge.paymentMethod)}
      </Chip>
    );
  }
  return (
    <Chip tone={chargeStatusChipTone(charge.status)}>
      {billingChargeStatusLabel(charge.status)}
    </Chip>
  );
}
